import { memo, useMemo, useEffect } from "react";
import { ScaffoldFooter } from "@repo/core/components/layout/Scaffolds/components/ScaffoldFooter";
import { useCountdown } from "@repo/core/hooks/useCountdown";
import { formatSecondsToMMSS } from "@repo/core/utils/time.utils";

interface ConfirmTokenFooterProps {
  handleSubmitForm: () => void;
  isSubmitting: boolean;
  handleResendCode: () => Promise<void>;
  isResendingToken: boolean;
  shouldEnableResend: boolean;
  setShouldEnableResend: (value: boolean) => void;
  countdownTime: number;
}

export const ConfirmTokenFooter = memo(
  ({
    handleSubmitForm,
    isSubmitting,
    handleResendCode,
    isResendingToken,
    shouldEnableResend,
    setShouldEnableResend,
    countdownTime
  }: ConfirmTokenFooterProps) => {
    const { start, currentTime, isFinished } = useCountdown(countdownTime);
    const normalizedCountdownTime = Math.max(0, countdownTime);
    const isWaitingToResend =
      normalizedCountdownTime > 0 && (!isFinished || shouldEnableResend);

    useEffect(() => {
      start();
    }, [start]);

    useEffect(() => {
      if (shouldEnableResend) {
        start();
        setShouldEnableResend(false);
      }
    }, [shouldEnableResend, start, setShouldEnableResend]);

    const secondaryText = useMemo(() => {
      if (!isWaitingToResend) return "Reenviar Código";

      return `Reenviar código em ${formatSecondsToMMSS(currentTime)}`;
    }, [currentTime, isWaitingToResend]);

    const onResendCode = () => {
      start();
      handleResendCode();
    };

    const handleSubmit = () => {
      if (isSubmitting) return;
      handleSubmitForm?.();
    };

    return (
      <ScaffoldFooter
        primaryText={secondaryText}
        primaryAction="darker-secondary"
        primaryVariant="outline"
        primaryIsDisabled={isWaitingToResend || isResendingToken}
        primaryOnPress={onResendCode}
        secondaryAction="primary"
        secondaryVariant="solid"
        secondaryOnPress={handleSubmit}
        secondaryIsLoading={isSubmitting}
        secondaryText="Continuar"
      />
    );
  }
);

ConfirmTokenFooter.displayName = "ConfirmTokenFooter";
